/**
 * Página de referencia con todas las reglas de validación que aplica el server
 * sobre los padrones. No requiere fetch — la doc está hardcodeada porque los
 * validadores también lo están (server: domain/validators/{row,cross-row,cuil}.ts).
 *
 * Cuando sumemos / cambiemos una regla del validator, actualizar esta página.
 */

interface Regla {
  campo?: string;
  rule: string;
  detalle: string;
}

interface Seccion {
  titulo: string;
  descripcion: string;
  reglas: Regla[];
}

const SECCIONES: Seccion[] = [
  {
    titulo: 'Formato y obligatoriedad de campos',
    descripcion:
      'Cada fila del padrón se valida campo por campo. Los errores de formato marcan la fila como `error`.',
    reglas: [
      { campo: 'LEGAJO', rule: 'required, alfanumérico, ≤20 caracteres', detalle: 'Identificador del legajo (mismo para todos los integrantes del grupo familiar). Letras, números y guiones; sin espacios ni caracteres especiales.' },
      { campo: 'PARENTESCO', rule: 'required, 0, 5 o 7', detalle: 'Se dan de alta 0 (Titular), 5 (Cónyuge) y 7 (Hijo). El 13 (Adherente) existe en el catálogo —hay adherentes ya afiliados que se muestran y se sincronizan— pero desde el 02/10/2026 no se pueden ingresar nuevos: la fila queda en error. Otros parentescos (abuelo, padre, hermano, suegro, etc.) NO se aceptan — la fila falla con error de format si trae otro código. No se aplica a padrones ya cerrados.' },
      { campo: 'APELLIDO', rule: 'required, alfabético, ≤30', detalle: 'Solo letras (acepta acentos, comillas, puntos, guiones).' },
      { campo: 'NOMBRES', rule: 'required, alfabético, ≤30', detalle: 'Mismas reglas que APELLIDO.' },
      { campo: 'FECHA NAC.', rule: 'required, dd/mm/aaaa', detalle: 'Fecha entre 01/01/1900 y hoy (hora argentina): no se aceptan fechas futuras. Mes 01-12 y día válido del mes.' },
      { campo: 'NACIONALIDAD', rule: 'required, AR o EX', detalle: 'Solo dos valores aceptados.' },
      { campo: 'SEXO', rule: 'required, M o F', detalle: '' },
      { campo: 'ESTADO CIVIL', rule: 'required, código del Anexo II', detalle: 'Códigos válidos (catálogo reducido por requerimiento Binary): S (Soltero), C (Casado), P (Separado/Divorciado). D, V y N ya no se aceptan.' },
      { campo: 'TIPO DOC.', rule: 'required, DNI o PA', detalle: '' },
      { campo: 'DOCUMENTO', rule: 'required, ≤15', detalle: 'Si TIPO DOC=DNI, solo dígitos. Sin puntos.' },
      { campo: 'CUIL', rule: 'required, 11 dígitos, dígito verificador válido', detalle: 'Numérico, 11 dígitos, sin puntos ni guiones. Además el dígito verificador (mod-11) debe cerrar: Binary rechaza con ORA-20012 los que no. Si no cierra, la fila queda en error con la sugerencia del CUIL correcto derivado de DNI + sexo. No se exige que el prefijo coincida con el sexo (puede ser 23/24 por homónimos), solo que el verificador sea consistente con el CUIL cargado.' },
      { campo: 'DOMICILIO', rule: 'required, ≤90', detalle: 'Calle.' },
      { campo: 'NÚMERO', rule: 'opcional, ≤7', detalle: 'Si viene vacío, el sistema lo autocompleta con "0" antes de persistir (Binary no acepta vacío ni "S/N").' },
      { campo: 'PISO / DEPTO', rule: 'opcional, ≤15', detalle: 'No obligatorios.' },
      { campo: 'CP', rule: 'opcional, ≤4 dígitos', detalle: 'Dejó de ser obligatorio en el padrón (28/05/2026). Se le exige al titular cuando valida sus datos en el wizard.' },
      { campo: 'ÁREA CELULAR', rule: 'requerida SI hay número de celular cargado, numérico, ≤4, sin 0 a la izquierda', detalle: 'Ej. 11 para CABA, 351 para Córdoba. Si NUMERO CELULAR está vacío, el área también puede ir vacía.' },
      { campo: 'NÚMERO CELULAR', rule: 'opcional, numérico, ≤8, sin el 15', detalle: '' },
      { campo: 'EMAIL', rule: 'required en titular · opcional en familiares (hereda del titular), formato válido, ≤60', detalle: 'Solo es obligatorio para PARENTESCO=0. Si un familiar viene con email vacío, al persistir el padrón se le copia el email del titular del mismo legajo. Si el familiar trae su propio email, se respeta sin sobrescribir.' },
    ],
  },
  {
    titulo: 'Reglas de fila con dependencias entre campos',
    descripcion:
      'Cruzan varios campos de la MISMA fila. Se aplican después del chequeo de formato individual. Los tres cortes de edad (hijo, titular, cónyuge) NO se aplican sobre padrones ya cerrados: son reglas posteriores a su cierre y marcarlas ahí sería retroactivo. En borrador y hasta la validación se aplican, que es el punto.',
    reglas: [
      {
        rule: 'nacionalidad.tipoDoc.mismatch',
        detalle:
          'NACIONALIDAD y TIPO DOC deben ser consistentes: AR ↔ DNI y EX ↔ PA. La validación se ejecuta después del chequeo de formato — si TIPO DOC es directamente inválido (ej. "LE"), no se evalúa esta regla.',
      },
      {
        rule: 'areaCelular.requiredIf',
        detalle:
          'Si NUMERO CELULAR tiene valor, ÁREA CELULAR es obligatoria. Si ambos vienen vacíos, se aceptan ambos.',
      },
      {
        rule: 'hijo.edad',
        detalle:
          'Un hijo (PARENTESCO=7) con 26 años o más queda en error: el plan cubre hijos hasta los 25 inclusive. El corte existía hasta el 28/05/2026, producto lo eliminó ese día y lo repuso el 24/08/2026 con el mismo valor. Un familiar mayor no entra como hijo; y como adherente (13) tampoco en esta etapa. No se aplica a padrones ya cerrados.',
      },
      {
        rule: 'titular.menor',
        detalle:
          'Un titular (PARENTESCO=0) con menos de 17 años queda en error. Mismo ida y vuelta que hijo.edad: existía hasta el 28/05/2026 y se repuso el 24/08/2026 con el valor original. Atrapa sobre todo el tipeo mal en la fecha que carga a un menor como jefe de hogar. No se aplica a padrones ya cerrados.',
      },
      {
        rule: 'conyuge.menorDeEdad',
        detalle:
          'Un cónyuge (PARENTESCO=5) menor de 18 años queda en error: es un parentesco mal cargado, casi siempre un hijo al que le pusieron 5 en vez de 7. Convive con los cortes de hijo y titular, repuestos el 24/08/2026. Adherente (13) es el único parentesco sin corte de edad. No se aplica a padrones ya cerrados. Si la FECHA NACIMIENTO no parsea o cae fuera de 1900..hoy, ninguna de las tres reglas de edad dispara: ya la marca el formato, y afirmar una edad sobre un dato roto sería inventar. Agosto 2026 — se encontraron 35 menores cargados como cónyuge, seis de ellos bebés nacidos ese año.',
      },
    ],
  },
  {
    titulo: 'Reglas de grupo familiar (cross-row dentro del padrón)',
    descripcion:
      'Se aplican después de validar fila por fila. Inyectan errores adicionales sobre las filas afectadas y pueden invalidar varias filas a la vez. Las que miran contra legajos que YA existen (group.titular.conflict, group.conyuge.conflict) y el email único de titular no se aplican sobre padrones ya cerrados, por el mismo motivo que los cortes de edad.',
    reglas: [
      {
        rule: 'group.titular.missing',
        detalle:
          'Cada legajo debe tener EXACTAMENTE un titular (PARENTESCO=0). Si un legajo tiene familiares pero ningún titular, el mensaje aclara que probablemente el LEGAJO del familiar no coincide con el del titular.',
      },
      {
        rule: 'group.titular.duplicate',
        detalle:
          'Si un legajo tiene >1 titular, las filas con PARENTESCO=0 quedan en error.',
      },
      {
        rule: 'group.titular.invalid',
        detalle:
          'Si la fila del titular tiene errores propios (formato, requeridos, etc.), todas las demás filas del mismo legajo también quedan en error. La idea: no se puede sumar familiares si la información del titular no es válida.',
      },
      {
        rule: 'group.conyuge.duplicate',
        detalle:
          'Cada legajo puede tener máximo UN cónyuge (PARENTESCO=5). Si hay más de uno EN EL ARCHIVO, todas esas filas quedan en error.',
      },
      {
        rule: 'group.titular.conflict',
        detalle:
          'Si el LEGAJO ya existe y su titular es OTRA persona, todo el grupo queda en error: un legajo tiene un solo titular, así que o el número está mal tipeado o esta persona necesita el suyo. El mensaje trae el DNI del titular actual. Agosto 2026 — antes el cierre lo resolvía solo y mal: si el legajo destino estaba en estado mergeable el titular nuevo desaparecía sin dejar rastro, y si estaba en flujo avanzado quedaba colgado del grupo familiar ajeno. No se aplica a padrones ya cerrados.',
      },
      {
        rule: 'group.conyuge.conflict',
        detalle:
          'Si el LEGAJO ya existe y YA tiene cónyuge, la fila del cónyuge del archivo queda en error (el resto del grupo puede entrar igual). El mensaje trae el DNI del cónyuge actual. Agosto 2026 — `group.conyuge.duplicate` solo mira dentro del archivo, así que un archivo con un único cónyuge pasaba aunque el legajo destino ya tuviera el suyo. No se aplica a padrones ya cerrados.',
      },
      {
        rule: 'mutación: estadoCivil forzado a C cuando hay cónyuge',
        detalle:
          'No genera error: el sistema fuerza automáticamente `estadoCivil = C` (Casado) tanto al titular como al cónyuge si el grupo tiene un PARENTESCO=5. Reunión 15/05 — Binary exige el estado correcto para la pareja. La regla anterior `group.conyuge.estadoCivil` ya no aplica (era error, ahora es mutación silenciosa).',
      },
      {
        rule: 'mutación: NUMERO domicilio autocompletado',
        detalle:
          'No genera error: si NUMERO viene vacío, se autocompleta con "0" antes de persistir (Binary no acepta vacío ni "S/N").',
      },
      {
        rule: 'mutación: email heredado del titular',
        detalle:
          'No genera error: antes de validar las filas, si un familiar tiene email vacío y el titular del mismo legajo tiene email, se copia automáticamente. Si el titular tampoco tiene email, no hay herencia (y el titular cae en `required`).',
      },
      {
        rule: 'unique (cuil)',
        detalle:
          'El CUIL no puede aparecer en dos filas del mismo padrón. Todas las filas que comparten CUIL quedan en error.',
      },
      {
        rule: 'unique (documento)',
        detalle:
          'El par (TIPO DOC, DOCUMENTO) no puede repetirse en el mismo padrón.',
      },
      {
        rule: 'unique (email titular)',
        detalle:
          'El EMAIL de un titular (PARENTESCO=0) no puede repetirse en otro titular del mismo padrón. Comparación case-insensitive. Los familiares NO cuentan (heredan/comparten el mail del titular). Las filas de titular que comparten email quedan en error.',
      },
    ],
  },
  {
    titulo: 'Unicidad cross-padrón',
    descripcion:
      'Una persona no puede figurar en dos padrones a la vez. Al subir un padrón nuevo (o recomputar uno en borrador) se cruza contra TODOS los padrones activos de la organización, sin importar el estado (borrador, pendiente de validación o cerrado) ni el parentesco (titular, cónyuge o hijo).',
    reglas: [
      {
        rule: 'unique.cross-padron',
        detalle:
          'Si un DNI ya figura en cualquier asociado de otro padrón de la empresa, la fila queda en error. El mensaje indica el padrón, número de legajo y rol (titular/cónyuge/hijo) con que aparece, y un link "Ir al padrón origen" para resolver el conflicto.',
      },
      {
        rule: 'unique.cross-padron (email titular)',
        detalle:
          'El EMAIL de un titular no puede coincidir con el de otro titular ya cargado en cualquier padrón de la empresa (incluye los dado de alta / en alta Binary). Solo titular-vs-titular, case-insensitive. La fila del titular queda en error e invalida su legajo hasta cambiar el mail.',
      },
    ],
  },
  {
    titulo: 'Seguridad — Rate limit de login',
    descripcion:
      'Protección anti-bruteforce. El bloqueo es por EMAIL, no por IP, para no penalizar a empleados que comparten la IP pública de la VPN corporativa (todos los usuarios de Medicus salen por la misma IP).',
    reglas: [
      {
        rule: 'login.rate-limit',
        detalle:
          'Si el mismo email tiene ≥4 intentos fallidos en los últimos 60 segundos, el siguiente intento se rechaza con HTTP 429 (LOGIN_BLOQUEADO) ANTES de tocar Keycloak. Configurable por env: LOGIN_MAX_FALLIDOS (default 4), LOGIN_VENTANA_SEG (default 60).',
      },
      {
        rule: 'login.reset-por-exito',
        detalle:
          'Un login exitoso resetea el contador del email — solo cuentan los fallos posteriores al último éxito.',
      },
      {
        rule: 'login.auditoria-ip',
        detalle:
          'Cada intento (exitoso o fallido) queda registrado en la colección `login_attempts` con email, IP del cliente (extraída de X-Forwarded-For), success, motivo y timestamp. TTL de 1 hora — los docs viejos se borran solos. La IP NO se usa para bloquear, solo para auditoría.',
      },
    ],
  },
  {
    titulo: 'Endpoints públicos del portal del titular (legacy)',
    descripcion:
      'Estas reglas las aplica el backend en los endpoints POST/PUT/DELETE de /portal/legajo/:token/familiar. La UI actual del wizard YA NO permite agregar ni eliminar familiares (sólo editar contacto y dirección, ver sección "Grupo familiar - paso 2"), así que estas validaciones quedan vivas sólo para consumo programático del endpoint. Documentadas por completitud.',
    reglas: [
      {
        rule: 'familiar.tipo',
        detalle:
          'Sólo se pueden agregar familiares con PARENTESCO=5 (cónyuge) o 7 (hijo). El 13 (adherente) no está habilitado en esta etapa (02/10/2026).',
      },
      {
        rule: 'familiar.documento.unico',
        detalle:
          'El DNI del nuevo familiar no puede coincidir con otro miembro del mismo legajo.',
      },
      {
        rule: 'conyuge.unico',
        detalle: 'Solo se puede tener UN cónyuge por legajo.',
      },
      {
        rule: 'hijo.fecha',
        detalle:
          'La fecha de nacimiento del hijo debe ser posterior a la del titular.',
      },
    ],
  },
  {
    titulo: 'Edición del titular (paso 1 del wizard)',
    descripcion:
      'Política nueva: el titular SOLO puede editar contacto y dirección. Cualquier otro dato es solo-lectura. Si está mal, lo reporta como observación.',
    reglas: [
      {
        rule: 'titular.editable',
        detalle:
          'Editables por el titular: domicilio (calle, número, piso, depto), código postal, característica de celular, número de celular, email.',
      },
      {
        rule: 'titular.domicilio.real',
        detalle:
          'El domicilio que carga el titular debe ser donde VIVE actualmente — no el que figura en el DNI ni el de la organización. La credencial física y notificaciones se envían a esta dirección.',
      },
      {
        rule: 'titular.no-editable',
        detalle:
          'NO se pueden modificar: documento, fecha de nacimiento, tipo de documento, CUIL, apellido, nombres, sexo, estado civil, nacionalidad. Cada uno tiene un botón "Reportar como incorrecto" que persiste el reporte en las observaciones del legajo.',
      },
    ],
  },
  {
    titulo: 'Grupo familiar (paso 2 del wizard)',
    descripcion:
      'El titular ya NO modifica la composición del grupo. Solo puede actualizar contacto y dirección de cada familiar. El titular tampoco se ve a sí mismo en este paso — sus propios datos los validó en el paso anterior.',
    reglas: [
      {
        rule: 'familia.solo-familiares',
        detalle:
          'La pantalla muestra UNICAMENTE a los familiares del grupo (parentesco ≠ 0). El titular NO aparece en este paso para no inducir a tocar de nuevo sus datos.',
      },
      {
        rule: 'familia.sin-familiares',
        detalle:
          'Si el legajo no tiene familiares cargados, en lugar de una lista vacía se muestra un aviso: "No tenés grupo familiar cargado. Si esto es un error, comunicate con el área de RRHH de tu organización". El titular sigue pudiendo continuar al siguiente paso.',
      },
      {
        rule: 'familia.solo-lectura',
        detalle:
          'El titular NO puede agregar ni eliminar familiares. La composición del grupo viene fijada por la carga del padrón.',
      },
      {
        rule: 'familia.editable',
        detalle:
          'Por cada familiar, el titular puede editar los mismos campos que para sí: domicilio completo, CP, característica/celular y email.',
      },
      {
        rule: 'familia.reportar',
        detalle:
          'Si un familiar no debería estar en el legajo, si falta alguien, o si algún dato bloqueado está mal, el titular usa el botón "Reportar como incorrecto" en el campo correspondiente.',
      },
    ],
  },
  {
    titulo: 'Observaciones del titular',
    descripcion:
      'Cada vez que el titular reporta un dato incorrecto, queda persistido en el legajo y visible para RRHH/Medicus en el backoffice.',
    reglas: [
      {
        rule: 'observaciones.persistencia',
        detalle:
          'Se guardan en `legajo.observaciones` con: campo reportado, asociadoId al que pertenece, mensaje del titular (hasta 500 chars) y timestamp.',
      },
      {
        rule: 'observaciones.visibilidad',
        detalle:
          'En la tabla del padrón, cada fila muestra las observaciones reportadas para ese integrante con un ícono de bandera amarilla. RRHH/Medicus resuelve corrigiendo desde Binary o subiendo un padrón de correcciones.',
      },
      {
        rule: 'observaciones.idempotencia',
        detalle:
          'Si el mismo campo ya fue reportado por el titular, el botón se reemplaza por un badge "Reportado" para evitar duplicados desde la UI. Igual el server acepta varios reportes del mismo campo (queda histórico).',
      },
    ],
  },
  {
    titulo: 'Cuestionario de salud',
    descripcion: 'Reglas del paso 3 del wizard.',
    reglas: [
      {
        rule: 'cuestionario.titularExclusivo',
        detalle:
          'La sección "Datos administrativos" (6 preguntas) sólo aplica al titular. Los familiares no la responden.',
      },
      {
        rule: 'cuestionario.condicional',
        detalle:
          'Hay preguntas que solo se exigen si la pregunta padre fue respondida con un valor específico (campo `dependeDe` en el catálogo).',
      },
      {
        rule: 'cuestionario.completo',
        detalle:
          'Para marcar el cuestionario como completo, TODAS las preguntas exigidas del integrante deben tener respuesta. Si alguna falta, el server devuelve 400 con la lista de faltantes.',
      },
      {
        rule: 'cuestionario.global',
        detalle:
          'Para finalizar el legajo, TODOS los integrantes (titular + familiares) deben tener su cuestionario marcado como completo.',
      },
    ],
  },
  {
    titulo: 'Cierre del padrón y validación Medicus',
    descripcion:
      'Cuando el usuario empresa cierra el padrón, NO habilita directo a los titulares. Hay un paso intermedio de revisión por admin Medicus.',
    reglas: [
      {
        rule: 'cierre.cero-errores',
        detalle:
          'Un padrón sólo se puede cerrar si tiene 0 filas en error. Si quedan errores, hay que corregirlos manualmente o subir un archivo de correcciones.',
      },
      {
        rule: 'cierre.estado-pendiente',
        detalle:
          'Al cerrar, el padrón pasa al estado `pendiente_validacion` y todos sus legajos se crean en estado `pendiente_validacion_medicus`. Los titulares no pueden entrar al portal hasta que Medicus valide.',
      },
      {
        rule: 'asociado.eliminar.borrador',
        detalle:
          'Mientras el padrón está en `borrador`, el usuario empresa puede sacar filas individuales con el botón "Eliminar" de cada fila (confirm nativo simple, sin modal). El backend recompone las inconsistencias y los conteos OK/error al toque. Una vez que el padrón sale de borrador (pendiente_validacion o cerrado), la acción se rechaza con 400 — para esos casos se usa DELETE /legajos/:id que mantiene la integridad del flujo.',
      },
      {
        rule: 'validacion.individual',
        detalle:
          'Un admin Medicus puede validar legajo por legajo desde el detalle del padrón. Botón "Validar" pasa el legajo a `creado` (queda listo para que el titular active con OTP).',
      },
      {
        rule: 'validacion.rechazo',
        detalle:
          'Alternativamente, el admin puede rechazar el legajo con un motivo obligatorio (hasta 500 chars). El legajo pasa a `rechazado_medicus` y, cuando el titular intente entrar, ve el motivo y se le pide que escale a RRHH.',
      },
      {
        rule: 'validacion.bulk',
        detalle:
          'Botón "Validar padrón completo (N pendientes)" valida todos los legajos pendientes del padrón en una sola acción.',
      },
      {
        rule: 'validacion.auto-cierre',
        detalle:
          'Cuando no quedan legajos en `pendiente_validacion_medicus` (todos validados o rechazados), el padrón pasa automáticamente a `cerrado`. No requiere acción adicional del admin.',
      },
      {
        rule: 'otp.gate',
        detalle:
          'Una vez validado el legajo (estado `creado`), el titular sigue necesitando validar OTP antes de acceder al wizard. El gate de OTP habilita la consulta paga a Renaper.',
      },
      {
        rule: 'otp.ttl',
        detalle:
          'OTP válido por 10 minutos, máximo 5 intentos. Tras OTP+Renaper OK, el legajo pasa a `en_validacion` y el titular recibe un activacionToken con TTL de 7 días.',
      },
    ],
  },
  {
    titulo: 'Pago del plan superador',
    descripcion:
      'El paso de pago solo aparece si el titular eligió MS2 en el wizard. MS1 no requiere pago — lo cubre la organización.',
    reglas: [
      {
        rule: 'pago.requerido-solo-ms2',
        detalle:
          'El step "Pago" se muestra únicamente cuando `planSuperadorElegido === true`. Si el titular eligió MS1 (o aún no decidió), se salta directo al cierre.',
      },
      {
        rule: 'pago.gateway-oracle',
        detalle:
          'Si ORACLE_PAGO_ENABLED=true, los pagos se procesan via el package PL/SQL WEB_GATEWAY_PAGO de Oracle TMED (mismo gateway que usa prospectos). El server inserta una intención de pago en SF_INTENCION_PAGO y devuelve la URL del checkout para redirigir al usuario. ORACLE_PAGO_CODIGO_PAYWAY (default 8) selecciona el medio de pago en el package.',
      },
      {
        rule: 'pago.intentos',
        detalle:
          'Máximo 3 intentos por legajo. Cada intento queda persistido en `legajo.pago.intentos` con estado, monto y motivo de rechazo.',
      },
      {
        rule: 'pago.aprobacion-bloquea-cambios',
        detalle:
          'Cuando un pago queda en estado `aprobado`, no se puede reintentar ni iniciar uno nuevo. El user solo puede continuar.',
      },
    ],
  },
  {
    titulo: 'Bajas — archivo y motivos',
    descripcion:
      'Se aplican sobre el archivo de bajas al previsualizarlo, y se REVALIDAN al confirmar (el cliente arma el payload y podría saltear la pantalla). La plantilla descargable trae estos mismos motivos y reglas en sus anexos.',
    reglas: [
      {
        campo: 'DNI',
        rule: 'required',
        detalle:
          'Única columna obligatoria del archivo. Solo dígitos. Si el DNI está repetido en el padrón, la fila queda `ambiguo` y hay que desambiguarla con la columna LEGAJO.',
      },
      {
        campo: 'MOTIVO',
        rule: 'required por fila, allowlist 36010 / 37020',
        detalle:
          'No hay motivo por defecto: si el archivo no lo trae, se elige en la revisión. Los códigos de la serie 200 (210, 212, 217, 220, 222…) están dados de baja en Binary hace ~10 años y NO se aceptan, aunque el motor los tome.',
      },
      {
        campo: 'FECHA_VIGENCIA',
        rule: 'required (salvo 36010), dd/mm/aaaa',
        detalle:
          'Desde cuándo corre la baja. Se puede cargar o corregir en la revisión con un calendario. Si la vigencia es del mes en curso o anterior, Binary aplica la baja al instante.',
      },
      {
        campo: 'FECHA_FALLECIMIENTO',
        rule: 'required si MOTIVO=37020, dd/mm/aaaa',
        detalle:
          'Fecha REAL del deceso. Se registra en Binary como dato adicional FFAL. Una fila 37020 sin esta fecha no se puede confirmar.',
      },
      {
        campo: 'LEGAJO',
        rule: 'opcional',
        detalle: 'Solo se usa para desambiguar un DNI que aparece más de una vez.',
      },
    ],
  },
  {
    titulo: 'Bajas — qué se procesa y qué va a Alertas',
    descripcion:
      'Cada persona se cruza EN VIVO contra Binary (por CUIL, afi_id o web_id). La clasificación decide si la baja sale automática, si hay que corregir un dato o si el caso requiere intervención manual.',
    reglas: [
      {
        rule: 'baja.integrante',
        detalle: 'Familiar activo en Binary → se da de baja (solicitud DSAFAM).',
      },
      {
        rule: 'baja.titular-solo',
        detalle:
          'Titular SIN familiares activos → el legajo es él solo, así que se da de baja el legajo completo (DSATIT).',
      },
      {
        rule: 'baja.grupo-completo',
        detalle:
          'Titular + TODOS sus integrantes activos vienen en el archivo → UNA sola solicitud de titular; Binary arrastra a los familiares por réplica (no llevan solicitud propia).',
      },
      {
        rule: 'baja.titular-con-familia-parcial → REARMADO',
        detalle:
          'Titular con parte de su familia activa fuera del archivo. No se puede bajar solo: hay que repartir antes al grupo que queda, y eso se hace desde la misma revisión con "Rearmar grupo". Si no se rearma, la fila queda en Alertas Bajas para gestionarse a mano — como todo lo que el lote no resuelve.',
      },
      {
        rule: 'rearmado.quien-puede-quedar-a-cargo',
        detalle:
          'Solo el cónyuge (sin condiciones) o un hijo de hasta 25 años. A partir de 26 solo con discapacidad declarada, y con curador. Menores de 18: pueden, pero con tutor. Adherentes y nietos NO. Tutor y curador todavía se cargan por el operador, así que esos casos siguen frenados.',
      },
      {
        rule: 'rearmado.reparto-completo',
        detalle:
          'Hay que decir con quién queda CADA integrante del grupo, y a un solo lado. Binary reemplaza la foto entera del grupo: el que no quede asignado se queda sin vínculo, o sea sin cobertura.',
      },
      {
        rule: 'rearmado.varios-grupos',
        detalle:
          'El reparto puede dejar más de un grupo (dos hijos que pasan a ser titulares independientes). El primero conserva el legajo original; cada grupo adicional se lleva un legajo nuevo derivado de ese.',
      },
      {
        rule: 'rearmado.orden-de-las-solicitudes',
        detalle:
          'Primero MODIF_TIT_PAREN (arma el o los grupos nuevos) y DESPUÉS la BAJA del saliente, ya como familiar. Al revés, la réplica de Binary arrastraría al grupo entero y le estamparía el motivo a toda la familia.',
      },
      {
        rule: 'rearmado.jerarquia-nj',
        detalle:
          'Quien queda a cargo entra como "no jerarquizado" (CATAFI NJ), no con la categoría del alta: un cambio de titularidad no es una venta y no genera comisión.',
      },
      {
        rule: 'rearmado.domicilio-heredado',
        detalle:
          'En MS ningún familiar tiene domicilio propio (lo heredan del titular) y Binary no promueve a nadie sin uno: se le copia el del saliente, que es la misma casa. Si no lo tiene ninguno de los dos, se corta antes de tocar Binary.',
      },
      {
        rule: 'vigencia.36010-forzada',
        detalle:
          'Con motivo "Baja pedida por empresa" la vigencia se fuerza SIEMPRE al 1° del mes próximo, ignorando la del archivo.',
      },
      {
        rule: 'vigencia.no-anterior-al-deceso',
        detalle:
          'Con 37020, la vigencia no puede ser anterior a la fecha de fallecimiento. Es un error de dato: se corrige en la revisión.',
      },
      {
        rule: 'plan.ms1-y-ms2',
        detalle:
          'MS1 y MS2 se dan de baja igual, y la deuda del grupo no frena la baja (desde el 23/10/2026; antes MS2 iba a Alertas). La guarda es una allowlist: si el plan no se pudo leer o es de otro convenio, va a Alertas como "Plan sin determinar" y no se da de baja. El plan se lee de la raíz del grupo (vive en el titular).',
      },
      {
        rule: 'alerta.fallecimiento-con-grupo',
        detalle:
          'Fallecimiento de un titular CON grupo activo. No se puede bajar de una: la réplica estamparía "fallecimiento" a toda la familia. Se resuelve con "Rearmar grupo" en la misma revisión; si nadie puede quedar a cargo, recién ahí va a Alertas Bajas.',
      },
      {
        rule: 'alerta.fallecimiento-manual',
        detalle:
          'Fallecimiento con deceso anterior al mes en curso, o vigencia anterior al 1° del mes próximo → caso manual → Alertas Bajas. El carril automático es siempre vigencia del mes próximo en adelante.',
      },
      {
        rule: 'alerta.datos-incompletos',
        detalle:
          'Las filas con motivo no habilitado, sin motivo, sin fecha de fallecimiento o con vigencia anterior al deceso se corrigen en la revisión. Si el lote se confirma sin corregirlas, quedan registradas en Alertas Bajas en vez de perderse.',
      },
      {
        rule: 'alerta.rechazo-binary',
        detalle:
          'Toda baja que Binary rechace queda en Alertas Bajas con el error devuelto.',
      },
      {
        rule: 'baja.ya-dada / no-encontrada',
        detalle:
          'Si la persona ya tiene baja espejada en el micrositio se omite (idempotencia). Si no se resuelve en Binary por ningún identificador, se informa y no se procesa.',
      },
    ],
  },
  {
    titulo: 'Bajas — alcance por organización',
    descripcion:
      'Un usuario de organización solo ve y da de baja a SU gente. Los roles Medicus operan sobre todo el padrón. El corte lo hace el backend, no la pantalla.',
    reglas: [
      {
        rule: 'baja.scope-lectura',
        detalle:
          'El listado, la búsqueda, la previsualización del archivo, Últimas bajas y Alertas se filtran por la organización del usuario DENTRO de la consulta. Una persona de otra organización es indistinguible de una que no existe: si el filtro se aplicara después, el mensaje "no tenés acceso" ya confirmaría que ese DNI está en otro padrón.',
      },
      {
        rule: 'baja.scope-escritura',
        detalle:
          'Confirmar una baja, marcar y desmarcar validan la organización de CADA persona antes de escribir, no solo al listar: el payload lo arma el cliente y podría traer cualquier id. Si una sola persona del lote es de otra organización, se rechaza el lote entero con 403.',
      },
      {
        rule: 'baja.scope-sin-organizacion',
        detalle:
          'Un usuario de organización que no tenga ninguna asignada recibe 403 — NO degrada a "ve todo". Es el caso más peligroso porque parece un problema de datos y sería una fuga.',
      },
    ],
  },
];

export function ReglasPage() {
  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">Reglas de validación</h1>
        <p className="text-element-mid">
          Todas las reglas que aplica el sistema sobre los padrones, los
          familiares, el cuestionario de salud y el cierre. Cada bloque agrupa reglas por
          categoría y muestra el mensaje que verá el operador cuando alguna
          falle.
        </p>
      </header>

      <div style={{ display: 'grid', gap: '1rem', marginTop: '1.5rem' }}>
        {SECCIONES.map((s) => (
          <article className="card" key={s.titulo}>
            <h2 className="heading-5">{s.titulo}</h2>
            <p
              className="text-element-mid"
              style={{ fontSize: 13, marginTop: 4, marginBottom: 12 }}
            >
              {s.descripcion}
            </p>
            <div className="tableWrap">
              <table className="dataTable">
                <thead>
                  <tr>
                    <th style={{ width: 140 }}>Campo</th>
                    <th style={{ width: 280 }}>Regla</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {s.reglas.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {r.campo ?? '—'}
                      </td>
                      <td>
                        <code style={{ fontWeight: 600 }}>{r.rule}</code>
                      </td>
                      <td style={{ fontSize: 13 }}>{r.detalle}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
